import React, { useMemo } from "react";

import { useAuthContext } from "../../../lib/context/Auth/AuthContext";
import { useMediaQuery } from "../../../lib/hooks/useMediaQuery";
import { usePeopleContext } from "../../../views/Users/People/lib/PeopleContext";

import { Status } from "../../shared/Status/Status";
import { trimText } from "../../../lib/helpers/trimText";
import { PeopleActions } from "../../shared/PeopleActions/PeopleActions";

import {
  Contact,
  SortDirection,
  SortField,
} from "../../../api/grpc/contacts/contacts";
import {
  Role,
  Status as StatusType,
} from "../../../api/grpc/contacts/account/account";

import {
  StructureTable,
  StructureTableColumn,
} from "../../shared/StructureTable/StructureTable";
import { Pagination } from "../../shared/Pagination/Pagination";
import { EntityType } from "../../../api/grpc/subscription/subscription";
import { LicenseSwitch } from "../../Workplaces/LicenseSwitch/LicenseSwitch";
import { PeopleRole } from "./PeopleRole";
import { IconDefinition } from "../../shared/Icon/Icon.generated";
import { Tooltip } from "../../shared/Tooltip/Tooltip";
import { LoginMethod } from "../../../api/grpc/account/account";

import "./PeopleTable.scss";

const columnToSortField: Record<string, SortField> = {
  emailId: SortField.EMAIL,
  name: SortField.NAME,
  role: SortField.ROLE,
  desk: SortField.DESK_NAME,
  groups: SortField.PERMISSION_GROUPS,
  status: SortField.STATUS,
  licensed: SortField.LICENSED,
};

interface Props {
  contacts: Contact[];
  refetch: () => void;
}

const PeopleRoleType: {
  [key in LoginMethod]: IconDefinition | undefined;
} = {
  "0": undefined,
  "1": undefined,
  "2": "google_wp",
  "3": "microsoft-logo",
};

const columns: StructureTableColumn[] = [
  {
    key: "emailId",
    header: "Email ID",
    sortable: true,
  },
  {
    key: "name",
    header: "Name",
    sortable: true,
  },
  {
    key: "role",
    header: "Role",
    sortable: true,
  },
  {
    key: "desk",
    header: "Desk",
    sortable: true,
  },
  {
    key: "groups",
    header: "Groups",
    sortable: true,
  },

  {
    key: "status",
    header: "Status",
    sortable: true,
  },
  {
    key: "licensed",
    header: "Licensed",
    sortable: true,
  },
  {
    key: "actions",
    header: "",
  },
];

export const PeopleTable = ({ contacts, refetch }: Props) => {
  const { sortField, sortDirection, setSort } = usePeopleContext();
  const { user } = useAuthContext();
  const smallScreen = useMediaQuery("(max-width: 1200px)");

  const sortedColumnKey = useMemo(
    () =>
      Object.keys(columnToSortField).find(
        (key) => columnToSortField[key] === sortField
      ) || "name",
    [sortField]
  );
  const tableSortDirection = sortDirection === SortDirection.DESC ? "desc" : "asc";

  const rows = contacts.map((item) => {
    const isNotActive = item.status === StatusType.INACTIVE;
    const userName = item.firstName + " " + item.lastName;
    const isCurrentUser = item.id === user?.claims.user_id;
    const listOfDesks = item.desks.map((desk) => " " + desk.deskName);
    const listOfGroups = item.permissionGroups.map((group) => " " + group.name);
    const hasSocialLogin =
      item.loginMethod === LoginMethod.GSUITE ||
      item.loginMethod === LoginMethod.O365;
    const isOwner = item.role === Role.OWNER;

    return {
      emailId: (
        <h4 className={isNotActive ? "inactive" : ""}>
          <Tooltip content={item.email || "-"}>
            {trimText(item.email || "-", smallScreen ? 100 : 35)}
          </Tooltip>
        </h4>
      ),
      name: (
        <h4 className={isNotActive ? "inactive" : ""}>
          <Tooltip content={userName}>
            {`${trimText(userName, smallScreen ? 100 : 25)}`}{" "}
            {isCurrentUser ? "(You)" : ""}
          </Tooltip>
        </h4>
      ),
      role: (
        <PeopleRole
          isNotActive={isNotActive}
          role={item.role}
          icon={hasSocialLogin ? PeopleRoleType[item.loginMethod] : undefined}
          loginMethod={item.loginMethod}
        />
      ),
      desk: (
        <h4 className={isNotActive ? "inactive" : ""}>
          <Tooltip content={String(listOfDesks) || "-"}>
            {trimText(String(listOfDesks) || "-", smallScreen ? 100 : 22)}
          </Tooltip>
        </h4>
      ),
      groups: (
        <h4 className={isNotActive ? "inactive" : ""}>
          <Tooltip content={String(listOfGroups) || "-"}>
            {trimText(String(listOfGroups) || "-", smallScreen ? 100 : 22)}
          </Tooltip>
        </h4>
      ),
      status: <Status status={item.status} />,
      licensed:
        item.role === Role.SUPPORT || item.role === Role.OWNER ? (
          <></>
        ) : (
          <LicenseSwitch
            id={item.id}
            value={item.licensed}
            type={EntityType.USER}
            refetch={refetch}
          />
        ),
      actions:
        isCurrentUser || isOwner ? <></> : <PeopleActions contact={item} />,
    };
  });

  const handleSort = (columnKey: string) => {
    const field = columnToSortField[columnKey];
    if (field === undefined) {
      return;
    }

    const nextDirection =
      field === sortField
        ? sortDirection === SortDirection.DESC
          ? SortDirection.ASC
          : SortDirection.DESC
        : SortDirection.DESC;

    setSort(field, nextDirection);
  };

  return (
    <div className="PeopleTable">
      <StructureTable
        small
        columns={columns}
        rows={rows}
        onSort={handleSort}
        sortedColumnKey={sortedColumnKey}
        sortDirection={tableSortDirection}
      />
      <Pagination />
    </div>
  );
};
